import { useStatsigClient, useStatsigUser } from '@statsig/react-bindings';
import { useMemo } from 'react';

import { FeatureGates } from 'modules/common/constants/featuregates';
import { Member } from 'modules/common/interfaces/member';
import { MembershipTier } from 'modules/common/types';
import isLaunchpadTaskBlocking from 'modules/common/components/Layout/LaunchpadTasks/useLaunchpadSchema/taskStatus';
import { menuAreas as menuAreasRaw } from 'modules/common/components/Layout/Sidebar/menuAreas';
import { DynamicConfigGate, MenuItem } from 'modules/common/components/Layout/Sidebar/Sidebar.interface';
import { useMember } from 'modules/common/queries/member';
import { GateEvaluation } from 'modules/common/useStatsigGate';
import { sanitizeMemberData } from 'modules/common/utils/sanitizeMemberData';
import usePersistData from 'modules/common/hooks/usePersistData';
import useOAuth from 'services/oauth/queries/useOAuth';
import usePayrollService from 'services/payroll/queries/usePayrollService';
import { usePayrollDocumentsSignatureQueueList } from 'services/payroll/api/generated/v1/payroll';
import { OAuthData } from 'services/oauth/queries/useOAuth/useOAuth.interface';
import useLaunchpadSchema from 'modules/common/components/Layout/LaunchpadTasks/useLaunchpadSchema';
import useCheckGate from 'modules/common/useCheckGate';
import useMemberTaskFeed from 'services/memberTasks/queries/useMemberTaskFeed';
import { isFrameworkMemberTaskFeedItem } from 'modules/Tasks/utils/memberTaskFeedItem';
import { useAccountingOnboardingProgress } from 'services/bookkeeping/queries/useAccountingOnboardingProgress';
import { isActivated, isInOnboardingStages } from 'modules/common/utils/memberStage';

const PAYROLL_DOCUMENTS_PATH = '/payroll/documents';

function createTaskCountBadge(count: number | undefined): MenuItem['badge'] {
    if (!count) {
        return undefined;
    }
    return { label: String(count), variant: 'filled' };
}

const toTaskArray = (requiredTask?: string | string[]): string[] => {
    if (!requiredTask) {
        return [];
    }
    return Array.isArray(requiredTask) ? requiredTask : [requiredTask];
};

const collectRequiredTasks = (items: MenuItem[]): string[] =>
    items.flatMap((item) => {
        const submenuTasks = item.submenu ? collectRequiredTasks(item.submenu) : [];
        return [...toTaskArray(item.requiredTask), ...submenuTasks];
    });

const REQUIRED_TASKS = new Set(collectRequiredTasks(menuAreasRaw));

/**
 * Custom hook to filter menu areas based on the logged user access to the feature gates
 */
export default function useMenuAreas() {
    const {
        client: { getDynamicConfig },
    } = useStatsigClient();
    const checkGate = useCheckGate();
    const { user } = useStatsigUser();
    const { getData: getOauthData, setData: setOauthData } = usePersistData<OAuthData>();
    const { getData: getMemberData, setData: setMemberData } = usePersistData<Partial<Member>>();
    const cachedOauth = getOauthData('oauth');
    const cachedMember = getMemberData('member');

    const { isLoading: isMemberLoading, data: member } = useMember({
        select: (data) => {
            // SECURITY FIX: Sanitize sensitive PII before caching to localStorage
            const sanitizedMember = sanitizeMemberData(data);
            setMemberData('member', sanitizedMember);
            return data;
        },
    });
    const { isLoading: isOAuthLoading } = useOAuth({
        select: (data) => {
            setOauthData('oauth', data);
            return data;
        },
    });

    const { data: onboardingProgress, isLoading: isOnboardingProgressLoading } = useAccountingOnboardingProgress();

    // Fall back to the cached member while useMember() refetches so onboarding status survives a
    // reload; the cache is sanitized but still carries `stage` and `usertype`. Everything gating on
    // activation reads this, not `member` — keying off `member` alone renders the sidebar unlocked for
    // the length of the member fetch, and the route guard 404s anyone who clicks in that window.
    const effectiveMember = member ?? cachedMember;
    const isMemberActivated = isActivated(effectiveMember?.stage);

    // Only fetch member tasks when the gate is on — the count is only displayed on the (also gated) /tasks menu item.
    const isMemberTasksUiEnabled = checkGate(FeatureGates.MemberTasksUiEnabled);
    // Count feed items, not flat tasks, so a group counts as one card (matching what the member
    // sees) rather than over-counting each task it bundles.
    const { items: taskFeedItems, isSuccess: isTaskFeedReady } = useMemberTaskFeed(undefined, {
        enabled: Boolean(isMemberTasksUiEnabled && effectiveMember?.id),
    });
    // Only count once both underlying queries have settled — otherwise the badge briefly shows a
    // partial total while one side is still loading.
    const frameworkTaskCount = useMemo(
        () => (isTaskFeedReady ? taskFeedItems.filter(isFrameworkMemberTaskFeedItem).length : 0),
        [isTaskFeedReady, taskFeedItems]
    );

    // Only core members see Payroll, so nobody else needs its setup state. While it loads, setup
    // reads as unfinished: the Payroll area briefly shows as a plain link rather than showing sub-pages
    // to a member who hasn't set up payroll and then taking them away.
    const { data: payrollService } = usePayrollService({
        enabled: effectiveMember?.membership_tier === MembershipTier.core,
    });
    // Same read as the Documents page's "Needs your signature" list, so signing there (which
    // invalidates this key) clears the badge without a reload. Only fetched once the Payroll
    // sub-pages can render.
    const { data: signatureQueue } = usePayrollDocumentsSignatureQueueList({
        query: { enabled: payrollService?.gep_eligible === true && checkGate(FeatureGates.PayrollDashboardV2) },
    });

    // Fetch launchpad items for menu item locking (built items have computed parent task status)
    const { launchpadItems, isPendingLaunchpadTasks } = useLaunchpadSchema({
        enabled: Boolean(isInOnboardingStages(effectiveMember?.stage) && REQUIRED_TASKS.size > 0),
    });

    const checkIsGateActive = (featureGate: GateEvaluation) => {
        const isGateActive = checkGate(featureGate.name) ?? false;
        return isGateActive === featureGate.value;
    };

    const checkIsDynamicConfigActive = ({ config, key, value }: DynamicConfigGate) =>
        getDynamicConfig(config).get(key, false) === value;

    const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
        return items.filter((item) => {
            const { customValidation } = item;
            let customValidationResult = true;

            if (customValidation) {
                if (!cachedMember || !cachedOauth) {
                    return false;
                }
                customValidationResult = customValidation(
                    cachedMember as Member,
                    cachedOauth,
                    onboardingProgress,
                    payrollService
                );
            }

            if (item.featureGate) {
                const gates = Array.isArray(item.featureGate) ? item.featureGate : [item.featureGate];
                if (!gates.every(checkIsGateActive)) {
                    return false;
                }
            }

            if (item.hideWhenGates?.length && item.hideWhenGates.every(checkIsGateActive)) {
                return false;
            }

            if (item.dynamicConfigGate) {
                const gates = Array.isArray(item.dynamicConfigGate) ? item.dynamicConfigGate : [item.dynamicConfigGate];
                if (!gates.every(checkIsDynamicConfigActive)) {
                    return false;
                }
            }

            return customValidationResult;
        });
    };

    /**
     * An area whose submenu has filtered down to one item (or none) becomes a plain link.
     *
     * Without this, gating every submenu entry off leaves a disclosure that expands to
     * nothing and a row whose click goes nowhere — and an area that only splits into a
     * submenu for a staged rollout would change navigation for everyone outside it. The
     * single surviving item's own path wins, so the area still lands where that item points.
     */
    const collapseEmptySubmenu = (area: MenuItem): MenuItem => {
        if (!area.submenu || area.submenu.length > 1) {
            return area;
        }

        const { submenu, basePath, ...rest } = area;
        return { ...rest, path: submenu[0]?.path ?? basePath ?? '' };
    };

    const menuAreas = useMemo<typeof menuAreasRaw>(() => {
        const filteredAreas = filterMenuItems(menuAreasRaw);

        return filteredAreas.map((area) => {
            if (area.submenu) {
                return collapseEmptySubmenu({ ...area, submenu: filterMenuItems(area.submenu) });
            }
            return area;
        });
    }, [checkGate, getDynamicConfig, user, cachedMember, cachedOauth, onboardingProgress, payrollService]);

    if ((!cachedOauth || !cachedMember) && (isMemberLoading || isOAuthLoading || isOnboardingProgressLoading)) {
        // To prevent pop into existence of the sidebar when the data is loaded
        return [];
    }

    // Add generic lock status to menu areas based on their required tasks.
    // A member who isn't activated yet always has launchpad tasks, so an empty list means we never got
    // them. Stay locked instead of reading every required task as absent and unlocking the row — the
    // health insurance route guard denies on the same signal, so the nav can't link into a 404.
    const hasNoLaunchpadData = !launchpadItems?.length;

    const applyLockStatus = (item: MenuItem): MenuItem => {
        if (!item.requiredTask) {
            return item;
        }
        const requiredTasks = toTaskArray(item.requiredTask);
        const hasBlockingTask = requiredTasks.some((taskId) => isLaunchpadTaskBlocking(launchpadItems, taskId));
        // Only lock once we have a member (live or cached) and they are NOT activated. Truthiness, not
        // `!== undefined`: an absent cache reads as null, and with no member at all we can't judge.
        const shouldApplyLocking = Boolean(effectiveMember) && !isMemberActivated;
        const isLocked = shouldApplyLocking && (isPendingLaunchpadTasks || hasNoLaunchpadData || hasBlockingTask);

        return {
            ...item,
            locked: isLocked,
            loading: shouldApplyLocking && isPendingLaunchpadTasks,
            // Resolve cohort-dependent copy here so consumers only ever see a plain string
            lockedTooltip:
                typeof item.lockedTooltip === 'function' ? item.lockedTooltip(effectiveMember) : item.lockedTooltip,
        };
    };

    // Lock applies to top-level areas (e.g. Accounting, Payroll) and submenu items (e.g. Insurance > Health)
    const menuAreasWithLockStatus = menuAreas?.map((area) => {
        const lockedArea = applyLockStatus(area);
        if (area.submenu) {
            return { ...lockedArea, submenu: area.submenu.map(applyLockStatus) };
        }
        return lockedArea;
    });

    const menuAreasWithTaskCount = menuAreasWithLockStatus?.map((area) => {
        if (area.path === '/tasks') {
            return { ...area, badge: createTaskCountBadge(frameworkTaskCount) };
        }
        if (area.submenu) {
            return {
                ...area,
                submenu: area.submenu.map((item) =>
                    item.path === PAYROLL_DOCUMENTS_PATH
                        ? { ...item, badge: createTaskCountBadge(signatureQueue?.count) }
                        : item
                ),
            };
        }
        return area;
    });

    return menuAreasWithTaskCount ?? [];
}
